import { apiHelpers } from './api';

// Types
export interface UserXPStats {
  userId: string;
  totalXp: number;
  level: number;
  xpForCurrentLevel: number;
  xpToNextLevel: number;
  progressPercent: number;
  levelTitle: string;
  rank: number;
}

export type XPSource =
  | 'EVENT_CREATED'
  | 'EVENT_ATTENDED'
  | 'EVENT_COMMENT'
  | 'EVENT_PHOTO_UPLOAD'
  | 'EXPENSE_PAID'
  | 'EXPENSE_SPLIT_SETTLED'
  | 'GAME_PLAYED'
  | 'GAME_WON'
  | 'KLOBI_JUMP_SCORE'
  | 'KLOBI_JUMP_HIGH_SCORE'
  | 'DRINKING_GAME_JOINED'
  | 'DRINKING_GAME_COMPLETED'
  | 'DRINKING_GAME_HOSTED'
  | 'POLL_CREATED'
  | 'POLL_VOTED'
  | 'BIRTHDAY_GIFT_GIVEN'
  | 'WISHLIST_ITEM_RESERVED'
  | 'ACHIEVEMENT_EARNED'
  | 'LEVEL_UP'
  | 'DAILY_LOGIN'
  | 'WEEKLY_STREAK'
  | 'MANUAL_ADJUSTMENT';

export interface XPTransaction {
  id: string;
  userId: string;
  amount: number;
  source: XPSource;
  sourceId: string | null;
  description: string | null;
  createdAt: string;
}

export interface XPBreakdown {
  source: XPSource;
  totalXP: number;
  count: number;
}

export interface LevelInfo {
  level: number;
  xpRequired: number;
  totalXpRequired: number;
  title: string;
}

export interface LevelsResponse {
  currentLevel: number;
  nextLevel: number;
  xpForNext: number;
  levelsInfo: LevelInfo[];
}

// API Functions
export const getMyXPStats = async (): Promise<UserXPStats> => {
  return apiHelpers.get<UserXPStats>('/xp/me');
};

export const getUserXPStats = async (userId: string): Promise<UserXPStats> => {
  return apiHelpers.get<UserXPStats>(`/xp/user/${userId}`);
};

export const getXPHistory = async (limit: number = 50): Promise<XPTransaction[]> => {
  return apiHelpers.get<XPTransaction[]>(`/xp/history?limit=${limit}`);
};

export const getXPBreakdown = async (): Promise<XPBreakdown[]> => {
  return apiHelpers.get<XPBreakdown[]>('/xp/breakdown');
};

export const getLevelsInfo = async (): Promise<LevelsResponse> => {
  return apiHelpers.get<LevelsResponse>('/xp/levels');
};

// Helper functions
export const getXPSourceLabel = (source: XPSource): string => {
  const labels: Record<XPSource, string> = {
    EVENT_CREATED: 'Event erstellt',
    EVENT_ATTENDED: 'Event besucht',
    EVENT_COMMENT: 'Kommentar',
    EVENT_PHOTO_UPLOAD: 'Foto hochgeladen',
    EXPENSE_PAID: 'Ausgabe bezahlt',
    EXPENSE_SPLIT_SETTLED: 'Schulden beglichen',
    GAME_PLAYED: 'Spiel gespielt',
    GAME_WON: 'Spiel gewonnen',
    KLOBI_JUMP_SCORE: 'Klobi Jump Score',
    KLOBI_JUMP_HIGH_SCORE: 'Neuer Highscore',
    DRINKING_GAME_JOINED: 'Trinkspiel beigetreten',
    DRINKING_GAME_COMPLETED: 'Trinkspiel beendet',
    DRINKING_GAME_HOSTED: 'Trinkspiel gehostet',
    POLL_CREATED: 'Umfrage erstellt',
    POLL_VOTED: 'Abgestimmt',
    BIRTHDAY_GIFT_GIVEN: 'Geschenk gegeben',
    WISHLIST_ITEM_RESERVED: 'Wunsch reserviert',
    ACHIEVEMENT_EARNED: 'Achievement freigeschaltet',
    LEVEL_UP: 'Level Up',
    DAILY_LOGIN: 'Taeglicher Login',
    WEEKLY_STREAK: 'Wochen-Streak',
    MANUAL_ADJUSTMENT: 'Admin-Anpassung',
  };
  return labels[source] || source;
};

export const getXPSourceIcon = (source: XPSource): string => {
  const icons: Record<XPSource, string> = {
    EVENT_CREATED: '📅',
    EVENT_ATTENDED: '🎉',
    EVENT_COMMENT: '💬',
    EVENT_PHOTO_UPLOAD: '📸',
    EXPENSE_PAID: '💰',
    EXPENSE_SPLIT_SETTLED: '✅',
    GAME_PLAYED: '🎮',
    GAME_WON: '🏆',
    KLOBI_JUMP_SCORE: '🐸',
    KLOBI_JUMP_HIGH_SCORE: '⭐',
    DRINKING_GAME_JOINED: '🍺',
    DRINKING_GAME_COMPLETED: '🎊',
    DRINKING_GAME_HOSTED: '👑',
    POLL_CREATED: '📊',
    POLL_VOTED: '🗳️',
    BIRTHDAY_GIFT_GIVEN: '🎁',
    WISHLIST_ITEM_RESERVED: '🛒',
    ACHIEVEMENT_EARNED: '🏅',
    LEVEL_UP: '⬆️',
    DAILY_LOGIN: '📆',
    WEEKLY_STREAK: '🔥',
    MANUAL_ADJUSTMENT: '⚙️',
  };
  return icons[source] || '✨';
};
